'use client';

import React, { useState, useMemo } from 'react';
import { Search, X, Plus, GitCompare, CircleCheckBig, CircleX } from 'lucide-react';
import { toast } from 'sonner';

interface CollegeData {
  id: string;
  name: string;
  shortName: string;
  state: string;
  type: string;
  nirf: number | null;
  naac: string;
  mbbsSeats: number;
  annualFees: number;
  established: number;
  cutoffAIQ: string;
  cutoffState: string;
  hostel: boolean;
  pgCourses: number;
  facultyRatio: string;
  beds: number;
  nabhAccredited: boolean;
  researchPapers: number;
  internationalCollabs: number;
  placementSupport: boolean;
  clinicalExposure: string;
  librarySize: string;
  labFacilities: string;
  sportsFacility: boolean;
  canteen: boolean;
  wifi: boolean;
}

const collegesDB: CollegeData[] = [
  {
    id: 'cmp-001', name: 'All India Institute of Medical Sciences, New Delhi', shortName: 'AIIMS New Delhi',
    state: 'Delhi', type: 'Central', nirf: 1, naac: 'A++', mbbsSeats: 107, annualFees: 1628,
    established: 1956, cutoffAIQ: '50–80', cutoffState: 'N/A', hostel: true, pgCourses: 42,
    facultyRatio: '1:4', beds: 2478, nabhAccredited: true, researchPapers: 1840, internationalCollabs: 24,
    placementSupport: true, clinicalExposure: 'Exceptional', librarySize: '1.2L books', labFacilities: 'World-class',
    sportsFacility: true, canteen: true, wifi: true,
  },
  {
    id: 'cmp-002', name: 'Maulana Azad Medical College', shortName: 'MAMC Delhi',
    state: 'Delhi', type: 'Government', nirf: 3, naac: 'A+', mbbsSeats: 250, annualFees: 28900,
    established: 1958, cutoffAIQ: '300–350', cutoffState: '120–180', hostel: true, pgCourses: 28,
    facultyRatio: '1:6', beds: 1200, nabhAccredited: true, researchPapers: 620, internationalCollabs: 8,
    placementSupport: true, clinicalExposure: 'Excellent', librarySize: '80K books', labFacilities: 'Excellent',
    sportsFacility: true, canteen: true, wifi: true,
  },
  {
    id: 'cmp-003', name: 'Seth GS Medical College & KEM Hospital', shortName: 'KEM Mumbai',
    state: 'Maharashtra', type: 'Government', nirf: 5, naac: 'A+', mbbsSeats: 200, annualFees: 42300,
    established: 1926, cutoffAIQ: '850–1100', cutoffState: '400–600', hostel: true, pgCourses: 32,
    facultyRatio: '1:5', beds: 1800, nabhAccredited: true, researchPapers: 780, internationalCollabs: 12,
    placementSupport: true, clinicalExposure: 'Excellent', librarySize: '95K books', labFacilities: 'Excellent',
    sportsFacility: false, canteen: true, wifi: true,
  },
  {
    id: 'cmp-004', name: 'Christian Medical College', shortName: 'CMC Vellore',
    state: 'Tamil Nadu', type: 'Deemed', nirf: 4, naac: 'A++', mbbsSeats: 100, annualFees: 760000,
    established: 1900, cutoffAIQ: '1200–2000', cutoffState: 'N/A', hostel: true, pgCourses: 38,
    facultyRatio: '1:3', beds: 2700, nabhAccredited: true, researchPapers: 1200, internationalCollabs: 18,
    placementSupport: true, clinicalExposure: 'Exceptional', librarySize: '1.1L books', labFacilities: 'World-class',
    sportsFacility: true, canteen: true, wifi: true,
  },
  {
    id: 'cmp-005', name: 'Kasturba Medical College, Manipal', shortName: 'KMC Manipal',
    state: 'Karnataka', type: 'Deemed', nirf: 14, naac: 'A+', mbbsSeats: 250, annualFees: 1900000,
    established: 1953, cutoffAIQ: '5000–11000', cutoffState: 'N/A', hostel: true, pgCourses: 36,
    facultyRatio: '1:5', beds: 2032, nabhAccredited: true, researchPapers: 890, internationalCollabs: 22,
    placementSupport: true, clinicalExposure: 'Very Good', librarySize: '90K books', labFacilities: 'Excellent',
    sportsFacility: true, canteen: true, wifi: true,
  },
  {
    id: 'cmp-006', name: 'Bangalore Medical College & Research Institute', shortName: 'BMCRI',
    state: 'Karnataka', type: 'Government', nirf: 12, naac: 'A', mbbsSeats: 250, annualFees: 55000,
    established: 1955, cutoffAIQ: '1800–2500', cutoffState: '900–1400', hostel: true, pgCourses: 22,
    facultyRatio: '1:7', beds: 1600, nabhAccredited: false, researchPapers: 420, internationalCollabs: 5,
    placementSupport: false, clinicalExposure: 'Good', librarySize: '65K books', labFacilities: 'Good',
    sportsFacility: false, canteen: true, wifi: false,
  },
];

const compareAttributes = [
  { id: 'attr-nirf', label: 'NIRF Ranking', key: 'nirf', format: (v: CollegeData) => v.nirf ? `#${v.nirf}` : 'Unranked', bestLow: true },
  { id: 'attr-naac', label: 'NAAC Grade', key: 'naac', format: (v: CollegeData) => v.naac, bestHigh: false },
  { id: 'attr-type', label: 'College Type', key: 'type', format: (v: CollegeData) => v.type, bestHigh: false },
  { id: 'attr-state', label: 'State', key: 'state', format: (v: CollegeData) => v.state, bestHigh: false },
  { id: 'attr-established', label: 'Established', key: 'established', format: (v: CollegeData) => String(v.established), bestLow: true },
  { id: 'attr-seats', label: 'MBBS Seats', key: 'mbbsSeats', format: (v: CollegeData) => String(v.mbbsSeats), bestHigh: true },
  { id: 'attr-fees', label: 'Annual Fees', key: 'annualFees', format: (v: CollegeData) => v.annualFees >= 100000 ? `₹${(v.annualFees / 100000).toFixed(1)}L` : `₹${v.annualFees.toLocaleString('en-IN')}`, bestLow: true },
  { id: 'attr-cutoffaiq', label: 'AIQ Cutoff Rank', key: 'cutoffAIQ', format: (v: CollegeData) => v.cutoffAIQ, bestHigh: false },
  { id: 'attr-cutoffstate', label: 'State Cutoff Rank', key: 'cutoffState', format: (v: CollegeData) => v.cutoffState, bestHigh: false },
  { id: 'attr-beds', label: 'Hospital Beds', key: 'beds', format: (v: CollegeData) => v.beds.toLocaleString('en-IN'), bestHigh: true },
  { id: 'attr-pg', label: 'PG Courses', key: 'pgCourses', format: (v: CollegeData) => `${v.pgCourses} courses`, bestHigh: true },
  { id: 'attr-faculty', label: 'Faculty:Student Ratio', key: 'facultyRatio', format: (v: CollegeData) => v.facultyRatio, bestHigh: false },
  { id: 'attr-research', label: 'Research Papers/yr', key: 'researchPapers', format: (v: CollegeData) => v.researchPapers.toLocaleString('en-IN'), bestHigh: true },
  { id: 'attr-collabs', label: 'International Collabs', key: 'internationalCollabs', format: (v: CollegeData) => String(v.internationalCollabs), bestHigh: true },
  { id: 'attr-clinical', label: 'Clinical Exposure', key: 'clinicalExposure', format: (v: CollegeData) => v.clinicalExposure, bestHigh: false },
  { id: 'attr-library', label: 'Library', key: 'librarySize', format: (v: CollegeData) => v.librarySize, bestHigh: false },
  { id: 'attr-labs', label: 'Lab Facilities', key: 'labFacilities', format: (v: CollegeData) => v.labFacilities, bestHigh: false },
  { id: 'attr-hostel', label: 'Hostel', key: 'hostel', format: (v: CollegeData) => v.hostel ? 'Available' : 'Not Available', bestHigh: false, isBool: true },
  { id: 'attr-nabh', label: 'NABH Accredited', key: 'nabhAccredited', format: (v: CollegeData) => v.nabhAccredited ? 'Yes' : 'No', bestHigh: false, isBool: true },
  { id: 'attr-placement', label: 'Placement Support', key: 'placementSupport', format: (v: CollegeData) => v.placementSupport ? 'Yes' : 'No', bestHigh: false, isBool: true },
  { id: 'attr-sports', label: 'Sports Facility', key: 'sportsFacility', format: (v: CollegeData) => v.sportsFacility ? 'Yes' : 'No', bestHigh: false, isBool: true },
  { id: 'attr-wifi', label: 'Campus WiFi', key: 'wifi', format: (v: CollegeData) => v.wifi ? 'Yes' : 'No', bestHigh: false, isBool: true },
];

export default function CompareCollegesClient() {
  const [selected, setSelected] = useState<CollegeData[]>([collegesDB[0], collegesDB[1]]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchResults = useMemo(() => {
    if (!searchQuery) return collegesDB;
    return collegesDB.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.state.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  const addCollege = (college: CollegeData) => {
    if (selected.find(c => c.id === college.id)) {
      toast.info('College already in comparison');
      return;
    }
    if (selected.length >= 4) {
      toast.error('Maximum 4 colleges can be compared at once');
      return;
    }
    setSelected(prev => [...prev, college]);
    setSearchOpen(false);
    setSearchQuery('');
    toast.success(`${college.shortName} added to comparison`);
  };

  const removeCollege = (id: string) => {
    setSelected(prev => prev.filter(c => c.id !== id));
  };

  const getBestValue = (attr: typeof compareAttributes[0], colleges: CollegeData[]) => {
    if (attr.key === 'nirf') {
      const vals = colleges.map(c => c.nirf ?? 9999);
      return Math.min(...vals);
    }
    if (attr.key === 'annualFees') {
      const vals = colleges.map(c => c.annualFees);
      return Math.min(...vals);
    }
    if (attr.key === 'mbbsSeats' || attr.key === 'beds' || attr.key === 'pgCourses' || attr.key === 'researchPapers' || attr.key === 'internationalCollabs') {
      const vals = colleges.map(c => (c as Record<string, unknown>)[attr.key] as number);
      return Math.max(...vals);
    }
    return null;
  };

  const isBest = (attr: typeof compareAttributes[0], college: CollegeData, colleges: CollegeData[]) => {
    const best = getBestValue(attr, colleges);
    if (best === null) return false;
    const val = (college as Record<string, unknown>)[attr.key];
    if (attr.key === 'nirf') return (college.nirf ?? 9999) === best;
    if (attr.key === 'annualFees') return college.annualFees === best;
    return val === best;
  };

  const gridCols = selected.length === 1 ? 'grid-cols-2' : selected.length === 2 ? 'grid-cols-3' : selected.length === 3 ? 'grid-cols-4' : 'grid-cols-5';

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full text-xs text-accent border border-accent/20 mb-4">
          <GitCompare size={13} />
          <span>College Comparator</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-3 tracking-tight">
          Compare <span className="gradient-text">Medical Colleges</span>
        </h1>
        <p className="text-muted-foreground text-base max-w-xl">
          Side-by-side comparison of up to 4 colleges across 22 attributes — fees, infrastructure, cutoffs, and more.
        </p>
      </div>

      {/* College selector row */}
      <div className={`grid ${gridCols} gap-4 mb-8`}>
        {/* Label column */}
        <div className="flex items-end pb-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Comparing</p>
        </div>

        {/* Selected colleges */}
        {selected.map((college) => (
          <div key={`sel-${college.id}`} className="glass-card rounded-2xl p-4 border border-primary/20 relative">
            <button
              onClick={() => removeCollege(college.id)}
              className="absolute top-3 right-3 p-1 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-danger transition-colors"
              aria-label="Remove college"
            >
              <X size={14} />
            </button>
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-sm mb-3">
              {college.nirf ? `#${college.nirf}` : 'N/A'}
            </div>
            <h3 className="text-foreground font-semibold text-sm leading-tight mb-1">{college.shortName}</h3>
            <p className="text-muted-foreground text-xs">{college.state}</p>
            <span className={`mt-2 inline-block text-xs px-2 py-0.5 rounded-md font-medium ${college.type === 'Government' || college.type === 'Central' ? 'badge-govt' : college.type === 'Deemed' ? 'badge-deemed' : 'badge-private'}`}>
              {college.type}
            </span>
          </div>
        ))}

        {/* Add college slot */}
        {selected.length < 4 && (
          <div className="relative">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-full h-full min-h-[120px] glass-card rounded-2xl border border-dashed border-white/20 hover:border-primary/40 flex flex-col items-center justify-center gap-2 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-primary/30 transition-colors">
                <Plus size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <p className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">Add College</p>
            </button>

            {/* Search dropdown */}
            {searchOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 z-20 glass-card rounded-xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="p-3 border-b border-white/5">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search colleges..."
                      className="input-glass pl-8 py-2 text-xs"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {searchResults.map(college => (
                    <button
                      key={`search-${college.id}`}
                      onClick={() => addCollege(college)}
                      disabled={!!selected.find(c => c.id === college.id)}
                      className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-b border-white/3 last:border-0"
                    >
                      <p className="text-sm text-foreground font-medium">{college.shortName}</p>
                      <p className="text-xs text-muted-foreground">{college.state} · {college.type} {college.nirf ? `· NIRF #${college.nirf}` : ''}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comparison table */}
      {selected.length >= 2 ? (
        <div className="glass-card rounded-2xl overflow-hidden border border-white/8">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              Comparing <span className="text-primary">{selected.length}</span> colleges across <span className="text-primary">22</span> attributes
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-success">
                <div className="w-2 h-2 rounded-full bg-success/30 border border-success/50" />
                Best value
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-white/10 border border-white/20" />
                Standard
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <colgroup>
                <col style={{ width: '200px' }} />
                {selected.map(c => <col key={`col-${c.id}`} />)}
              </colgroup>
              <thead>
                <tr className="border-b border-white/5 bg-white/2">
                  <th className="text-left py-3 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Attribute</th>
                  {selected.map(college => (
                    <th key={`th-${college.id}`} className="text-center py-3 px-4 text-xs font-semibold text-foreground">
                      {college.shortName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compareAttributes.map((attr, attrIdx) => (
                  <tr key={attr.id} className={`border-b border-white/4 table-row-hover transition-colors ${attrIdx % 2 === 0 ? '' : 'bg-white/1'}`}>
                    <td className="py-3 px-5 text-sm text-muted-foreground font-medium">{attr.label}</td>
                    {selected.map(college => {
                      const best = isBest(attr, college, selected);
                      const value = attr.format(college);
                      const isBoolAttr = attr.isBool;
                      const isPositive = isBoolAttr && (value === 'Available' || value === 'Yes');
                      const isNegative = isBoolAttr && (value === 'Not Available' || value === 'No');
                      return (
                        <td key={`cell-${college.id}-${attr.id}`} className="py-3 px-4 text-center">
                          <div className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                            best
                              ? 'bg-success/10 text-success border border-success/20'
                              : isPositive
                              ? 'bg-accent/10 text-accent border border-accent/10'
                              : isNegative
                              ? 'bg-danger/10 text-danger border border-danger/10' :'text-foreground'
                          }`}>
                            {isBoolAttr && isPositive && <CircleCheckBig size={12} />}
                            {isBoolAttr && isNegative && <CircleX size={12} />}
                            {best && !isBoolAttr && <CircleCheckBig size={12} className="text-success" />}
                            <span className="tabular-nums">{value}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="glass-card rounded-2xl p-16 text-center border border-white/8">
          <GitCompare size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-foreground font-semibold text-lg mb-2">Add at least 2 colleges to compare</h3>
          <p className="text-muted-foreground text-sm">Click the &ldquo;Add College&rdquo; button above to select colleges for comparison</p>
        </div>
      )}
    </div>
  );
}